import { IReservation, IRestaurantConfiguration } from '@localina/core';
import { PeopleIcon } from '@localina/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getTablesNamesByIds } from '../../utils/TablePlanService';
import { GuestStatusIcon } from '../GuestStatusIcon';
import ReservationOriginIcon from '../ReservationOriginIcon/ReservationOriginIcon';
import ReservationStatusActionIcon from '../ReservationStatus/ReservationStatusActionIcon';
import { ReservationTableNumbers } from '../ReservationTableNumbers';
import { ReservationAllergyLine } from '../ReservationAllergyLine';
import ReservationAreaCodes from './ReservationAreaCodes';
import { ReservationNoteLine } from './ReservationNoteLine';

interface IProps {
    reservation: IReservation;
    restaurantConfiguration: IRestaurantConfiguration;
    canUseTablePlans: boolean;
    canUseAreas: boolean;
    createdAtLabel?: string;
}

// The phone layout (mock-up "Reservationen mobil"): name and status on top, first name and company
// under it, then the values, then the notes. Allergies are spelled out rather than shown as an icon,
// and never truncated, so they can't be missed at any width.
export const ReservationCompactRow: React.FC<IProps> = ({
    reservation,
    restaurantConfiguration,
    canUseTablePlans,
    canUseAreas,
    createdAtLabel,
}) => {
    const { t } = useTranslation();
    const { guestInfo } = reservation;

    return (
        <div className="reservation-compact">
            <div className="reservation-compact__head">
                <div className="reservation-compact__who">
                    <div className="reservation-compact__name-line">
                        {Boolean(guestInfo?.guestStatus) && (
                            <span className="reservation-compact__guest-status">
                                <GuestStatusIcon guestStatus={guestInfo?.guestStatus} />
                            </span>
                        )}
                        <p className="reservation-compact__name">
                            {guestInfo ? guestInfo.lastName : t('reservations.view.fields.walkin')}
                        </p>
                    </div>
                    {Boolean(guestInfo?.firstName || guestInfo?.company) && (
                        <p className="reservation-compact__sub">
                            {Boolean(guestInfo?.firstName) && <b>{guestInfo!.firstName}</b>}
                            {Boolean(guestInfo?.firstName && guestInfo?.company) && ' · '}
                            {guestInfo?.company}
                        </p>
                    )}
                </div>
                <ReservationStatusActionIcon
                    reservation={reservation}
                    className="reservation-compact__status reservation-status-box"
                />
            </div>
            <div className="reservation-compact__facts">
                <span className="reservation-compact__fact people">
                    <PeopleIcon />
                    <span>{reservation.participants}</span>
                </span>
                {canUseAreas && (
                    <span className="reservation-compact__fact area">
                        <ReservationAreaCodes reservation={reservation} />
                    </span>
                )}
                {canUseTablePlans && Boolean(reservation.tableIds) && (
                    <span className="reservation-compact__fact tables">
                        <ReservationTableNumbers
                            tableNumbers={getTablesNamesByIds(
                                reservation.tableIds,
                                restaurantConfiguration.virtualAreas,
                            )}
                        />
                    </span>
                )}
                {Boolean(reservation.auditInfo) && (
                    <span className="reservation-compact__fact created">
                        <span className="reservation-origin-wrapper">
                            <ReservationOriginIcon reservation={reservation} />
                        </span>
                        {createdAtLabel}
                    </span>
                )}
            </div>
            {Boolean(guestInfo?.allergies) && (
                <ReservationAllergyLine allergies={guestInfo!.allergies!} className="reservation-compact__allergies" />
            )}
            {Boolean(reservation.comment || guestInfo?.restaurantComment) && (
                <div className="reservation-compact__notes">
                    {Boolean(reservation.comment) && <ReservationNoteLine kind="remark" text={reservation.comment!} />}
                    {Boolean(guestInfo?.restaurantComment) && (
                        <ReservationNoteLine kind="guest" text={guestInfo!.restaurantComment!} />
                    )}
                </div>
            )}
        </div>
    );
};
